/**
 * Normalize a presence row from WS snapshot/update into store shape.
 * Returns null when the payload is unusable (fail soft).
 */

function toUserId(value) {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    const n = Number(value);
    if (Number.isSafeInteger(n) && n > 0) return n;
  }
  return null;
}

function toIsoOrNull(value) {
  if (value == null || value === "") return null;
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return value;
}

export function normalizePresenceRow(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const userId = toUserId(raw.user_id);
  if (userId == null) return null;
  if (typeof raw.online !== "boolean") return null;

  const lastSeenAt = toIsoOrNull(raw.last_seen_at);
  const leaseExpiresAt = toIsoOrNull(raw.lease_expires_at);
  const updatedAtMs = Date.now();

  return {
    userId,
    online: raw.online,
    lastSeenAt,
    leaseExpiresAt: raw.online ? leaseExpiresAt : null,
    updatedAtMs,
  };
}

export function normalizePresenceSnapshot(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }
  const rows = Array.isArray(payload.presences) ? payload.presences : null;
  if (!rows) return [];
  const out = [];
  for (const row of rows) {
    const normalized = normalizePresenceRow(row);
    if (normalized) out.push(normalized);
  }
  return out;
}

/**
 * Prefer fresher evidence: later lease_expires_at wins when both online;
 * otherwise later updatedAtMs / last_seen_at wins.
 */
export function shouldReplacePresence(current, incoming) {
  if (!current) return true;
  if (!incoming) return false;
  if (current.userId !== incoming.userId) return true;

  if (incoming.online && current.online) {
    const curExp = current.leaseExpiresAt ? Date.parse(current.leaseExpiresAt) : 0;
    const nextExp = incoming.leaseExpiresAt ? Date.parse(incoming.leaseExpiresAt) : 0;
    if (nextExp && curExp && nextExp < curExp) {
      return false;
    }
    if (nextExp && curExp && nextExp > curExp) {
      return true;
    }
  }

  if (incoming.updatedAtMs < current.updatedAtMs) {
    return false;
  }

  if (
    !incoming.online
    && current.online
    && incoming.lastSeenAt
    && current.lastSeenAt
  ) {
    const curSeen = Date.parse(current.lastSeenAt) || 0;
    const nextSeen = Date.parse(incoming.lastSeenAt) || 0;
    if (nextSeen < curSeen) {
      return false;
    }
  }

  return true;
}
